import React, { useEffect, useMemo, useState } from "react";
import { Tree, Input, Switch, Button, Tag, Typography, Spin, Empty, Space, Tabs } from "antd";
import {
  SearchOutlined, FolderOutlined, TagOutlined, ApartmentOutlined, BgColorsOutlined,
  ExpandOutlined, CompressOutlined,
} from "@ant-design/icons";

const { Text, Paragraph } = Typography;

// Renders standarts/symbology/symbologyTree.json (generated by
// server/scripts/buildSymbologyTree.js from the OpenRail Designer content-management XML
// exports in standarts/symbology/transportation/) as a searchable tree, for BEP symbology
// submissions.
//
// ORD's own model is Feature Definition > Feature Symbology > Element Template > Level, but
// Feature Definition and Feature Symbology are the same real-world object split across two
// export files (same folder path + name) — so they're merged into one row here instead of
// forced into two tiers that would just repeat the same name. Element Template and Level stay
// as their own tiers: Level is shown as a tag on the Element Template row rather than a further
// nested tier, since a template almost always resolves to exactly one level and a bare tag
// reads better than a single-child branch.

const FOLDER_KEY = (parentKey, name) => `${parentKey}/f:${name}`;
const FEATURE_KEY = (parentKey, name) => `${parentKey}/n:${name}`;
const TEMPLATE_KEY = (featureKey, relationship, i) => `${featureKey}/t:${relationship}:${i}`;

function countFeatures(nodes) {
  let n = 0;
  for (const node of nodes) {
    if (node.type === "feature") n++;
    else n += countFeatures(node.children);
  }
  return n;
}

// Drops folders with nothing left under them and features that don't pass the current
// filters — pure data pass, no React involved, so the render pass below stays simple.
function filterTree(nodes, showUnmapped, term) {
  const out = [];
  for (const node of nodes) {
    if (node.type === "folder") {
      const children = filterTree(node.children, showUnmapped, term);
      if (children.length === 0) continue;
      out.push({ ...node, children });
    } else {
      if (!showUnmapped && !node.hasSymbology) continue;
      if (term && !node.name.toLowerCase().includes(term) && !(node.description || "").toLowerCase().includes(term)) continue;
      out.push(node);
    }
  }
  return out;
}

function highlight(text, term) {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "rgba(79,156,249,0.45)", color: "inherit", borderRadius: 2 }}>{text.slice(idx, idx + term.length)}</mark>
      {text.slice(idx + term.length)}
    </>
  );
}

function FeatureTitle({ node, term }) {
  return (
    <span className="sym-row sym-row-feature">
      <TagOutlined className="sym-icon" />
      <span className="sym-name">{highlight(node.name, term)}</span>
      {node.featureType && <Tag className="sym-tag-type">{node.featureType}</Tag>}
      {!node.hasSymbology && <Tag color="warning" className="sym-tag-flag">no symbology</Tag>}
      {node.description && <Text type="secondary" className="sym-desc">{node.description}</Text>}
    </span>
  );
}

function TemplateTitle({ tpl }) {
  return (
    <span className="sym-row sym-row-template">
      <ApartmentOutlined className="sym-icon" />
      <span className="sym-name">{tpl.label}</span>
      {tpl.levels.length > 0 ? (
        tpl.levels.map((lvl) => (
          <Tag key={lvl} className="sym-tag-level" icon={<BgColorsOutlined />}>{lvl}</Tag>
        ))
      ) : (
        <Tag className="sym-tag-flag">unresolved</Tag>
      )}
    </span>
  );
}

function FolderTitle({ name, count }) {
  return (
    <span className="sym-row sym-row-folder">
      <FolderOutlined className="sym-icon" />
      <span className="sym-name">{name}</span>
      <span className="sym-count">{count}</span>
    </span>
  );
}

function toAntdItems(nodes, parentKey, term) {
  return nodes.map((node) => {
    if (node.type === "folder") {
      const key = FOLDER_KEY(parentKey, node.name);
      return {
        key,
        title: <FolderTitle name={node.name} count={countFeatures(node.children)} />,
        children: toAntdItems(node.children, key, term),
      };
    }
    const key = FEATURE_KEY(parentKey, node.name);
    const templates = node.children || [];
    return {
      key,
      title: <FeatureTitle node={node} term={term} />,
      isLeaf: templates.length === 0,
      children: templates.length
        ? templates.map((tpl, i) => ({
            key: TEMPLATE_KEY(key, tpl.relationship, i),
            isLeaf: true,
            title: <TemplateTitle tpl={tpl} />,
          }))
        : undefined,
    };
  });
}

function collectAllKeys(items, acc = []) {
  for (const item of items) {
    acc.push(item.key);
    if (item.children) collectAllKeys(item.children, acc);
  }
  return acc;
}

function DisciplineTreeView({ disciplineKey, rawChildren, showUnmapped, term }) {
  const items = useMemo(
    () => toAntdItems(filterTree(rawChildren, showUnmapped, term), disciplineKey, term),
    [rawChildren, showUnmapped, term, disciplineKey]
  );
  const allKeys = useMemo(() => collectAllKeys(items), [items]);
  const [manualExpanded, setManualExpanded] = useState([]);
  const searching = term.length > 0;
  const expandedKeys = searching ? allKeys : manualExpanded;

  if (items.length === 0) {
    return <Empty description="No matching features" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;
  }

  return (
    <>
      {!searching && (
        <Space size={8} style={{ marginBottom: 10 }}>
          <Button size="small" icon={<ExpandOutlined />} onClick={() => setManualExpanded(allKeys)}>Expand all</Button>
          <Button size="small" icon={<CompressOutlined />} onClick={() => setManualExpanded([])}>Collapse all</Button>
        </Space>
      )}
      <Tree
        className="sym-tree"
        showLine={{ showLeafIcon: false }}
        blockNode
        treeData={items}
        expandedKeys={expandedKeys}
        onExpand={(keys) => setManualExpanded(keys)}
        height={560}
      />
    </>
  );
}

export default function SymbologyTree() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showUnmapped, setShowUnmapped] = useState(false);

  useEffect(() => {
    fetch("/api/symbology/transportation")
      .then((r) => r.json())
      .then((d) => setData(d.disciplines || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const term = searchInput.trim().toLowerCase();

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin size="large" /></div>;
  }
  if (error || !data || data.length === 0) {
    return <Empty description="Symbology tree not available — run `npm run build-symbology` on the server" style={{ marginTop: 40 }} />;
  }

  const tabItems = data.map((d) => {
    const total  = countFeatures(d.children);
    const mapped = countFeatures(filterTree(d.children, false, ""));
    return {
      key: d.key,
      label: `${d.label} (${mapped}/${total})`,
      children: (
        <DisciplineTreeView
          disciplineKey={d.key}
          rawChildren={d.children}
          showUnmapped={showUnmapped}
          term={term}
        />
      ),
    };
  });

  return (
    <div>
      <style>{`
        /* Feature rows (icon + name + tags + description, all inline) can run wider than a
           phone screen — contained here with its own horizontal scroll instead of blowing out
           the page's width, same pattern as .md-table-wrap in MarkdownContent.js. */
        .sym-tree { overflow-x: auto; }
        .sym-tree .ant-tree-treenode { padding: 3px 0; }
        .sym-row { display: inline-flex; align-items: center; gap: 8px; }
        .sym-icon { color: rgba(255,255,255,0.35); font-size: 13px; }
        .sym-row-feature .sym-icon { color: #4f9cf9; }
        .sym-name { font-family: 'DM Mono', monospace; font-size: 12.5px; }
        .sym-row-folder .sym-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600; font-size: 13px; }
        .sym-count {
          font-family: 'DM Mono', monospace; font-size: 10.5px; color: rgba(255,255,255,0.35);
          background: rgba(255,255,255,0.06); border-radius: 999px; padding: 1px 8px;
        }
        .sym-tag-type { margin: 0; font-size: 10px; line-height: 16px; padding: 0 6px; }
        .sym-tag-flag { margin: 0; font-size: 10px; line-height: 16px; padding: 0 6px; }
        .sym-tag-level { margin: 0; font-size: 10.5px; line-height: 18px; font-family: 'DM Mono', monospace; }
        .sym-desc { font-size: 11.5px; font-style: italic; max-width: 420px; }
      `}</style>

      <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 18, maxWidth: 760 }}>
        ORD hierarchy: <strong>Feature Definition → Feature Symbology → Element Template → Level</strong>.
        Definition and Symbology share the same name/folder path across two ORD exports, so they're
        merged into one row here; Element Template and the DGN Level it resolves to are each their own tier.
        Rows flagged <Tag color="warning" style={{ margin: "0 2px" }}>no symbology</Tag> have a Feature
        Definition with nothing mapped to a level yet.
      </Paragraph>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Search features…"
          prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.3)" }} />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          style={{ width: 260 }}
        />
        <Space size={8} align="center">
          <Switch size="small" checked={showUnmapped} onChange={setShowUnmapped} />
          <Text type="secondary" style={{ fontSize: 12.5 }}>Show features with no symbology</Text>
        </Space>
      </Space>

      <Tabs items={tabItems} />
    </div>
  );
}
